import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function GuidePage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const title = t('seo.guide.title');
    const description = t('seo.guide.description');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', t('seo.guide.keywords'));
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/tokyo-minpaku-guide`);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/tokyo-minpaku-guide`);
    setMeta('meta[name="last-modified"]', 'content', '2025-07-14');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    const schemaId = 'schema-guide';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      url: `${SITE_URL}/tokyo-minpaku-guide`,
      datePublished: '2026-01-01',
      dateModified: '2025-07-14',
      author: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/tokyo-minpaku-guide`,
      },
    });

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-8 shadow-xl">
              <i className="ri-book-open-line text-5xl text-white"></i>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 mb-6 leading-tight">
              Tokyo Minpaku Startup Guide
            </h1>
            <div className="inline-block bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-2 rounded-full text-lg font-bold mb-8">
              2026 Edition
            </div>
            <p className="text-xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Your complete guide to launching a successful Airbnb business in Tokyo. Written by active hosts with real licensing and operational experience.
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-white border-b-2 border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-serif font-bold text-slate-900 mb-6 text-center">
            What's Inside This Guide
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: 'ri-file-list-3-line', text: 'Difference between Minpaku and Ryokan License', link: '#license-difference' },
              { icon: 'ri-roadmap-line', text: 'Basic Licensing Steps', link: '#licensing-steps' },
              { icon: 'ri-time-line', text: 'Estimated Timeline', link: '#timeline' },
              { icon: 'ri-error-warning-line', text: 'Common Mistakes to Avoid', link: '#common-mistakes' },
              { icon: 'ri-money-dollar-circle-line', text: 'Revenue Estimation Overview', link: '#revenue-estimation' },
              { icon: 'ri-team-line', text: 'When to Choose Management Service', link: '#management-service' },
            ].map((item, index) => (
              <a
                key={index}
                href={item.link}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-lg hover:border-amber-300 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-xl text-white`}></i>
                </div>
                <p className="text-slate-800 font-medium">{item.text}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Section 1: License Difference */}
      <section id="license-difference" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <i className="ri-file-list-3-line text-3xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                Difference between Minpaku and Ryokan License
              </h2>
              <p className="text-slate-600 mt-1">Understanding the two main licensing options in Tokyo</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-600 p-6 rounded-r-xl mb-8">
              <p className="text-slate-800 font-medium mb-0">
                <strong>Key Insight:</strong> Choosing the right license type is the most important decision you'll make. It affects your operating days, revenue potential, and regulatory requirements.
              </p>
            </div>

            <h3 className="text-2xl font-serif font-bold text-slate-900 mt-8 mb-4">Minpaku License (民泊)</h3>
            
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 mb-6">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Legal Basis</h4>
              <p className="text-slate-700 mb-4">
                Governed by the <strong>Housing Accommodation Business Act</strong> (住宅宿泊事業法), enacted in June 2018. This law was created specifically to regulate short-term rentals like Airbnb.
              </p>
              
              <h4 className="text-xl font-bold text-slate-900 mb-4 mt-6">Key Characteristics</h4>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-amber-600 text-sm"></i>
                  </div>
                  <span><strong>Operating Limit:</strong> Maximum 180 days per year (approximately 6 months)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-amber-600 text-sm"></i>
                  </div>
                  <span><strong>Application Process:</strong> Notification-based (届出制) - simpler than Ryokan</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-amber-600 text-sm"></i>
                  </div>
                  <span><strong>Property Type:</strong> Must be a residential property (住宅)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-amber-600 text-sm"></i>
                  </div>
                  <span><strong>Processing Time:</strong> Typically 2-3 months</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-amber-600 text-sm"></i>
                  </div>
                  <span><strong>Cost:</strong> Lower initial investment (¥50,000-¥150,000 including fees)</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-serif font-bold text-slate-900 mt-12 mb-4">Ryokan License (旅館業)</h3>
            
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 mb-6">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Legal Basis</h4>
              <p className="text-slate-700 mb-4">
                Governed by the <strong>Hotel Business Act</strong> (旅館業法), a traditional hospitality industry regulation that has existed since 1948. This is the same license used by hotels and traditional inns.
              </p>
              
              <h4 className="text-xl font-bold text-slate-900 mb-4 mt-6">Key Characteristics</h4>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <span><strong>Operating Limit:</strong> No limit - operate 365 days per year</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <span><strong>Application Process:</strong> License-based (許可制) - more complex requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <span><strong>Property Type:</strong> Must meet specific facility standards (fire safety, ventilation, etc.)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <span><strong>Processing Time:</strong> Typically 4-6 months (longer due to inspections)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <span><strong>Cost:</strong> Higher initial investment (¥200,000-¥500,000+ including renovations)</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-serif font-bold text-slate-900 mt-12 mb-4">Comparison Table</h3>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                    <th className="px-6 py-4 text-left text-white font-bold">Factor</th>
                    <th className="px-6 py-4 text-left text-white font-bold">Minpaku License</th>
                    <th className="px-6 py-4 text-left text-white font-bold">Ryokan License</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="px-6 py-4 font-semibold text-slate-900">Operating Days</td>
                    <td className="px-6 py-4 text-slate-700">Max 180 days/year</td>
                    <td className="px-6 py-4 text-slate-700">365 days/year</td>
                  </tr>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <td className="px-6 py-4 font-semibold text-slate-900">Application Type</td>
                    <td className="px-6 py-4 text-slate-700">Notification (届出)</td>
                    <td className="px-6 py-4 text-slate-700">License (許可)</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="px-6 py-4 font-semibold text-slate-900">Processing Time</td>
                    <td className="px-6 py-4 text-slate-700">2-3 months</td>
                    <td className="px-6 py-4 text-slate-700">4-6 months</td>
                  </tr>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <td className="px-6 py-4 font-semibold text-slate-900">Initial Cost</td>
                    <td className="px-6 py-4 text-slate-700">¥50,000-¥150,000</td>
                    <td className="px-6 py-4 text-slate-700">¥200,000-¥500,000+</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="px-6 py-4 font-semibold text-slate-900">Property Requirements</td>
                    <td className="px-6 py-4 text-slate-700">Residential property</td>
                    <td className="px-6 py-4 text-slate-700">Facility standards required</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 font-semibold text-slate-900">Best For</td>
                    <td className="px-6 py-4 text-slate-700">Side income, testing market</td>
                    <td className="px-6 py-4 text-slate-700">Full-time business</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-600 p-6 rounded-r-xl mt-8">
              <h4 className="text-lg font-bold text-slate-900 mb-2">Which Should You Choose?</h4>
              <p className="text-slate-800 mb-0">
                <strong>Choose Minpaku</strong> if you want to test the market, have a residential property, or plan to use it as side income. <strong>Choose Ryokan</strong> if you're committed to full-time operation, want maximum revenue potential, and can meet facility requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Licensing Steps */}
      <section id="licensing-steps" className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <i className="ri-roadmap-line text-3xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                Basic Licensing Steps
              </h2>
              <p className="text-slate-600 mt-1">Step-by-step process for both license types</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Minpaku Steps */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-amber-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
                  <i className="ri-home-4-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-bold text-slate-900">Minpaku License Process</h3>
              </div>

              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Property Verification',
                    description: 'Confirm your property meets residential requirements and check local zoning regulations.',
                    duration: '1-2 weeks'
                  },
                  {
                    step: 2,
                    title: 'Document Preparation',
                    description: 'Gather property deed, floor plans, fire safety equipment certificates, and management contracts.',
                    duration: '2-3 weeks'
                  },
                  {
                    step: 3,
                    title: 'Notification Submission',
                    description: 'Submit notification to local government (ward office or city hall) with all required documents.',
                    duration: '1 day'
                  },
                  {
                    step: 4,
                    title: 'Review Period',
                    description: 'Government reviews your application. They may request additional documents or clarifications.',
                    duration: '4-6 weeks'
                  },
                  {
                    step: 5,
                    title: 'Notification Number Issued',
                    description: 'Receive your official notification number (届出番号). You can now legally operate.',
                    duration: '1 day'
                  },
                  {
                    step: 6,
                    title: 'Platform Registration',
                    description: 'Register your notification number with Airbnb and other booking platforms.',
                    duration: '1-2 days'
                  }
                ].map((item) => (
                  <div key={item.step} className="relative pl-12">
                    <div className="absolute left-0 top-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-full text-white font-bold">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-700 text-sm mb-2">{item.description}</p>
                      <span className="inline-block bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">
                        {item.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t-2 border-amber-200">
                <p className="text-center text-slate-700 font-semibold">
                  Total Time: <span className="text-amber-600 text-xl">2-3 months</span>
                </p>
              </div>
            </div>

            {/* Ryokan Steps */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-emerald-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                  <i className="ri-hotel-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-bold text-slate-900">Ryokan License Process</h3>
              </div>

              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Pre-Consultation',
                    description: 'Meet with local health department to confirm facility requirements and feasibility.',
                    duration: '1-2 weeks'
                  },
                  {
                    step: 2,
                    title: 'Facility Preparation',
                    description: 'Install required fire safety equipment, ventilation systems, and meet structural standards.',
                    duration: '4-8 weeks'
                  },
                  {
                    step: 3,
                    title: 'Document Preparation',
                    description: 'Prepare detailed floor plans, equipment certificates, building confirmation, and business plan.',
                    duration: '2-3 weeks'
                  },
                  {
                    step: 4,
                    title: 'License Application',
                    description: 'Submit application to local health department with all required documents and fees.',
                    duration: '1 day'
                  },
                  {
                    step: 5,
                    title: 'Facility Inspection',
                    description: 'Government inspector visits property to verify compliance with all facility standards.',
                    duration: '2-4 weeks'
                  },
                  {
                    step: 6,
                    title: 'Corrections (if needed)',
                    description: 'Address any issues found during inspection and request re-inspection if necessary.',
                    duration: '2-4 weeks'
                  },
                  {
                    step: 7,
                    title: 'License Issued',
                    description: 'Receive your official Ryokan license (許可番号). You can now legally operate.',
                    duration: '1-2 weeks'
                  },
                  {
                    step: 8,
                    title: 'Platform Registration',
                    description: 'Register your license number with Airbnb and other booking platforms.',
                    duration: '1-2 days'
                  }
                ].map((item) => (
                  <div key={item.step} className="relative pl-12">
                    <div className="absolute left-0 top-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full text-white font-bold">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-700 text-sm mb-2">{item.description}</p>
                      <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full">
                        {item.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t-2 border-emerald-200">
                <p className="text-center text-slate-700 font-semibold">
                  Total Time: <span className="text-emerald-600 text-xl">4-6 months</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Timeline */}
      <section id="timeline" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <i className="ri-time-line text-3xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                Estimated Timeline
              </h2>
              <p className="text-slate-600 mt-1">Visual timeline with key milestones</p>
            </div>
          </div>

          {/* Timeline Visualization */}
          <div className="relative">
            {/* Minpaku Timeline */}
            <div className="mb-16">
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
                  <i className="ri-home-4-line text-xl text-white"></i>
                </div>
                Minpaku License Timeline (2-3 Months)
              </h3>
              
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 to-orange-600"></div>
                
                <div className="space-y-8">
                  {[
                    { week: 'Week 1-2', title: 'Property Verification & Initial Research', icon: 'ri-search-line', color: 'amber' },
                    { week: 'Week 3-5', title: 'Document Preparation & Collection', icon: 'ri-file-text-line', color: 'amber' },
                    { week: 'Week 6', title: 'Notification Submission', icon: 'ri-send-plane-line', color: 'amber' },
                    { week: 'Week 7-10', title: 'Government Review Period', icon: 'ri-government-line', color: 'amber' },
                    { week: 'Week 11', title: 'Notification Number Issued', icon: 'ri-checkbox-circle-line', color: 'emerald' },
                    { week: 'Week 12', title: 'Platform Registration & Launch', icon: 'ri-rocket-line', color: 'emerald' }
                  ].map((item, index) => (
                    <div key={index} className="relative pl-20">
                      <div className={`absolute left-0 w-16 h-16 flex items-center justify-center bg-gradient-to-br from-${item.color}-500 to-${item.color}-600 rounded-xl shadow-lg`}>
                        <i className={`${item.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="bg-gradient-to-r from-slate-50 to-white border-2 border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xl font-bold text-slate-900">{item.title}</h4>
                          <span className="bg-amber-100 text-amber-800 text-sm font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                            {item.week}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Ryokan Timeline */}
            <div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                  <i className="ri-hotel-line text-xl text-white"></i>
                </div>
                Ryokan License Timeline (4-6 Months)
              </h3>
              
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-500 to-teal-600"></div>
                
                <div className="space-y-8">
                  {[
                    { week: 'Week 1-2', title: 'Pre-Consultation with Health Department', icon: 'ri-discuss-line', color: 'emerald' },
                    { week: 'Week 3-10', title: 'Facility Preparation & Renovations', icon: 'ri-tools-line', color: 'emerald' },
                    { week: 'Week 11-13', title: 'Document Preparation', icon: 'ri-file-text-line', color: 'emerald' },
                    { week: 'Week 14', title: 'License Application Submission', icon: 'ri-send-plane-line', color: 'emerald' },
                    { week: 'Week 15-18', title: 'Facility Inspection Scheduled', icon: 'ri-search-eye-line', color: 'emerald' },
                    { week: 'Week 19-22', title: 'Corrections & Re-inspection (if needed)', icon: 'ri-tools-fill', color: 'amber' },
                    { week: 'Week 23-24', title: 'License Issued', icon: 'ri-checkbox-circle-line', color: 'emerald' },
                    { week: 'Week 25', title: 'Platform Registration & Launch', icon: 'ri-rocket-line', color: 'emerald' }
                  ].map((item, index) => (
                    <div key={index} className="relative pl-20">
                      <div className={`absolute left-0 w-16 h-16 flex items-center justify-center bg-gradient-to-br from-${item.color}-500 to-${item.color === 'emerald' ? 'teal' : 'orange'}-600 rounded-xl shadow-lg`}>
                        <i className={`${item.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="bg-gradient-to-r from-slate-50 to-white border-2 border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xl font-bold text-slate-900">{item.title}</h4>
                          <span className="bg-emerald-100 text-emerald-800 text-sm font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                            {item.week}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-slate-50 to-slate-100 border-2 border-slate-300 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-lightbulb-line text-amber-600"></i>
              Timeline Tips
            </h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1"></i>
                <strong>Start Early:</strong> Begin the process at least 3-6 months before your desired launch date
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1"></i>
                <strong>Buffer Time:</strong> Add 20-30% extra time for unexpected delays or document revisions
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1"></i>
                <strong>Seasonal Factors:</strong> Government offices may be slower during Golden Week, Obon, and year-end holidays
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1"></i>
                <strong>Professional Help:</strong> Using a management service can reduce timeline by 30-40%
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 4: Common Mistakes */}
      <section id="common-mistakes" className="py-20 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-red-500 to-orange-600 rounded-xl">
              <i className="ri-error-warning-line text-3xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                Common Mistakes to Avoid
              </h2>
              <p className="text-slate-600 mt-1">Learn from others' experiences</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: 'ri-building-line',
                title: 'Not Checking Building Management Rules',
                description: 'Many apartment buildings prohibit short-term rentals in their management rules (管理規約). Always check with your building management before starting the licensing process.',
                impact: 'High',
                solution: 'Obtain written permission from building management before investing time and money.'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Ignoring Local Zoning Restrictions',
                description: 'Some Tokyo wards have additional restrictions on Minpaku operations (e.g., residential-only zones, specific operating days). Ota City, Shinjuku, and Shibuya have unique rules.',
                impact: 'High',
                solution: 'Research your ward\'s specific regulations on their official website or consult with local government.'
              },
              {
                icon: 'ri-fire-line',
                title: 'Inadequate Fire Safety Equipment',
                description: 'Missing or expired fire extinguishers, smoke detectors, or emergency lighting. This is the #1 reason for Ryokan license rejections.',
                impact: 'Critical',
                solution: 'Hire a certified fire safety company to inspect and install all required equipment before application.'
              },
              {
                icon: 'ri-file-damage-line',
                title: 'Incomplete Documentation',
                description: 'Missing floor plans, outdated property deeds, or unsigned management contracts. Government offices will reject incomplete applications immediately.',
                impact: 'Medium',
                solution: 'Use a checklist and have all documents reviewed by a professional before submission.'
              },
              {
                icon: 'ri-calendar-line',
                title: 'Underestimating Timeline',
                description: 'Expecting to launch within 1 month. Many hosts sign lease agreements before getting licenses, wasting rent money during the application period.',
                impact: 'Medium',
                solution: 'Plan for 3-6 months minimum. Don\'t sign property leases until you have your license number.'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Not Budgeting for Hidden Costs',
                description: 'Forgetting about fire safety equipment (¥50,000-¥150,000), professional cleaning setup, furniture, and ongoing management fees.',
                impact: 'Medium',
                solution: 'Budget at least ¥500,000-¥1,000,000 for initial setup beyond licensing fees.'
              },
              {
                icon: 'ri-user-line',
                title: 'No Management Plan',
                description: 'Thinking you can manage everything yourself while working full-time. Guest communication, cleaning coordination, and emergency response require significant time.',
                impact: 'High',
                solution: 'Decide on management approach before launching: self-manage, partial service, or full management.'
              },
              {
                icon: 'ri-calculator-line',
                title: 'Unrealistic Revenue Expectations',
                description: 'Expecting 100% occupancy year-round. Tokyo Minpaku averages 60-70% occupancy, with seasonal fluctuations.',
                impact: 'Medium',
                solution: 'Use conservative estimates: 60% occupancy, ¥8,000-¥12,000 per night for 1-bedroom apartments.'
              },
              {
                icon: 'ri-shield-cross-line',
                title: 'No Insurance Coverage',
                description: 'Operating without proper liability insurance. Standard home insurance doesn\'t cover commercial short-term rental activities.',
                impact: 'Critical',
                solution: 'Purchase Minpaku-specific insurance (民泊保険) from providers like Sompo Japan or Tokio Marine.'
              },
              {
                icon: 'ri-translate-2',
                title: 'Poor Guest Communication System',
                description: 'Not having 24/7 emergency contact or multilingual support. This is a legal requirement for Minpaku operations.',
                impact: 'High',
                solution: 'Set up automated messaging systems and partner with multilingual support services.'
              },
              {
                icon: 'ri-account-box-line',
                title: 'Not Registering as a Business',
                description: 'Forgetting to file tax returns or register as a sole proprietor (個人事業主). Minpaku income is taxable and must be reported.',
                impact: 'High',
                solution: 'Register with your local tax office and keep detailed records of all income and expenses.'
              },
              {
                icon: 'ri-community-line',
                title: 'Ignoring Neighbor Relations',
                description: 'Not informing neighbors about your Minpaku operation. Neighbor complaints are the #1 reason for license revocations.',
                impact: 'Critical',
                solution: 'Inform neighbors in advance, provide your contact information, and establish house rules for guests.'
              }
            ].map((mistake, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 border-2 border-slate-200 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex-shrink-0">
                    <i className={`${mistake.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{mistake.title}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                      mistake.impact === 'Critical' ? 'bg-red-100 text-red-800' :
                      mistake.impact === 'High' ? 'bg-orange-100 text-orange-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {mistake.impact} Impact
                    </span>
                  </div>
                </div>
                <p className="text-slate-700 mb-4">{mistake.description}</p>
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-600 p-4 rounded-r-lg">
                  <p className="text-sm text-slate-800">
                    <strong className="text-emerald-700">Solution:</strong> {mistake.solution}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white border-2 border-amber-300 rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4 flex items-center gap-3">
              <i className="ri-shield-check-line text-emerald-600 text-3xl"></i>
              How to Avoid These Mistakes
            </h3>
            <p className="text-slate-700 mb-6">
              The best way to avoid these common pitfalls is to work with experienced professionals who have successfully navigated the licensing process multiple times. Our team has helped dozens of hosts launch their Tokyo Minpaku businesses without delays or rejections.
            </p>
            <a
              href="/minpaku-registration-tokyo"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white text-lg font-bold px-10 py-5 rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-right-circle-line text-2xl"></i>
              Learn About Our Support Services
            </a>
          </div>
        </div>
      </section>

      {/* Section 5: Revenue Estimation */}
      <section id="revenue-estimation" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
              <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                Revenue Estimation Overview
              </h2>
              <p className="text-slate-600 mt-1">Realistic income projections for Tokyo Minpaku</p>
            </div>
          </div>

          {/* Revenue Calculator Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Minpaku Revenue */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
                  <i className="ri-home-4-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-bold text-slate-900">Minpaku License Revenue</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-slate-900 mb-3">Example: 1-Bedroom Apartment in Shibuya</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Nightly Rate:</span>
                      <span className="font-bold text-slate-900">¥10,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Occupancy Rate:</span>
                      <span className="font-bold text-slate-900">65%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Operating Days:</span>
                      <span className="font-bold text-slate-900">180 days/year</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Occupied Nights:</span>
                      <span className="font-bold text-slate-900">117 nights</span>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-amber-300"></div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-bold text-slate-900">Gross Revenue:</span>
                    <span className="text-2xl font-bold text-amber-600">¥1,170,000</span>
                  </div>
                  
                  <h5 className="font-bold text-slate-900 mb-3">Expenses:</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-700">Cleaning (¥5,000 × 117):</span>
                      <span className="text-slate-900">¥585,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700">Platform Fees (15%):</span>
                      <span className="text-slate-900">¥175,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700">Utilities (¥15,000/mo):</span>
                      <span className="text-slate-900">¥180,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700">Insurance & Misc:</span>
                      <span className="text-slate-900">¥50,000</span>
                    </div>
                    <div className="flex justify-between font-bold pt-2 border-t border-amber-300">
                      <span className="text-slate-900">Total Expenses:</span>
                      <span className="text-slate-900">¥990,500</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl p-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Net Profit (Annual):</span>
                    <span className="text-3xl font-bold">¥179,500</span>
                  </div>
                  <p className="text-sm mt-2 opacity-90">Approximately ¥15,000/month</p>
                </div>
              </div>
            </div>

            {/* Ryokan Revenue */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                  <i className="ri-hotel-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-bold text-slate-900">Ryokan License Revenue</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-slate-900 mb-3">Example: 1-Bedroom Apartment in Shibuya</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Nightly Rate:</span>
                      <span className="font-bold text-slate-900">¥10,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Occupancy Rate:</span>
                      <span className="font-bold text-slate-900">65%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Operating Days:</span>
                      <span className="font-bold text-slate-900">365 days/year</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700">Occupied Nights:</span>
                      <span className="font-bold text-slate-900">237 nights</span>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-emerald-300"></div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-bold text-slate-900">Gross Revenue:</span>
                    <span className="text-2xl font-bold text-emerald-600">¥2,370,000</span>
                  </div>
                  
                  <h5 className="font-bold text-slate-900 mb-3">Expenses:</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-700">Cleaning (¥5,000 × 237):</span>
                      <span className="text-slate-900">¥1,185,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700">Platform Fees (15%):</span>
                      <span className="text-slate-900">¥355,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700">Utilities (¥15,000/mo):</span>
                      <span className="text-slate-900">¥180,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700">Insurance & Misc:</span>
                      <span className="text-slate-900">¥80,000</span>
                    </div>
                    <div className="flex justify-between font-bold pt-2 border-t border-emerald-300">
                      <span className="text-slate-900">Total Expenses:</span>
                      <span className="text-slate-900">¥1,800,500</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl p-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Net Profit (Annual):</span>
                    <span className="text-3xl font-bold">¥569,500</span>
                  </div>
                  <p className="text-sm mt-2 opacity-90">Approximately ¥47,500/month</p>
                </div>
              </div>
            </div>
          </div>

          {/* Factors Affecting Revenue */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-2 border-slate-300 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-6">
              Key Factors Affecting Your Revenue
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: 'ri-map-pin-line',
                  title: 'Location',
                  description: 'Central Tokyo (Shibuya, Shinjuku, Minato) commands 30-50% higher rates than outer wards. Proximity to stations is critical.'
                },
                {
                  icon: 'ri-calendar-event-line',
                  title: 'Seasonality',
                  description: 'Peak seasons (cherry blossom, autumn leaves, year-end) can double your rates. Summer and winter are slower.'
                },
                {
                  icon: 'ri-home-smile-line',
                  title: 'Property Quality',
                  description: 'Modern, well-designed spaces with quality amenities achieve 20-30% higher occupancy and rates.'
                },
                {
                  icon: 'ri-star-line',
                  title: 'Reviews & Rating',
                  description: 'Properties with 4.8+ ratings and 20+ reviews see 40% higher booking rates than new listings.'
                },
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'Pricing Strategy',
                  description: 'Dynamic pricing based on demand, events, and competition can increase revenue by 25-35%.'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Guest Experience',
                  description: 'Fast response times, clear communication, and thoughtful touches lead to repeat bookings and referrals.'
                }
              ].map((factor, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex-shrink-0">
                    <i className={`${factor.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900 mb-2">{factor.title}</h4>
                    <p className="text-slate-700 text-sm">{factor.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reality Check */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-600 p-8 rounded-r-2xl">
            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-4 flex items-center gap-3">
              <i className="ri-alert-line text-amber-600 text-3xl"></i>
              Reality Check: Important Considerations
            </h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1 flex-shrink-0"></i>
                <strong>These are gross profit estimates.</strong> You still need to pay income tax (5-45% depending on your bracket) and potentially consumption tax.
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1 flex-shrink-0"></i>
                <strong>Rent is not included.</strong> If you're renting the property, subtract ¥100,000-¥200,000/month for a 1-bedroom in central Tokyo.
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1 flex-shrink-0"></i>
                <strong>First year is typically slower.</strong> New listings take 3-6 months to build reviews and achieve optimal occupancy.
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1 flex-shrink-0"></i>
                <strong>Time investment matters.</strong> Self-managing requires 10-20 hours/week. Factor in your hourly value.
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-amber-600 mt-1 flex-shrink-0"></i>
                <strong>Market conditions change.</strong> Tokyo's Minpaku market has become more competitive since 2018. Quality and service differentiate winners.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 6: Management Service */}
      <section id="management-service" className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <i className="ri-team-line text-3xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                When to Choose a Management Service
              </h2>
              <p className="text-slate-600 mt-1">Decision criteria and comparison</p>
            </div>
          </div>

          {/* Decision Matrix */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Self-Management */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-slate-200">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl mb-6 mx-auto">
                <i className="ri-user-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 text-center mb-4">Self-Management</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Best For:</h4>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Single property owners
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Flexible schedule
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Hands-on personality
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Budget-conscious
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Time Required:</h4>
                  <p className="text-2xl font-bold text-slate-900">10-20 hrs/week</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Cost:</h4>
                  <p className="text-2xl font-bold text-emerald-600">¥0</p>
                  <p className="text-xs text-slate-600">(except cleaning)</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-slate-900">Responsibilities:</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Guest communication</li>
                  <li>• Booking management</li>
                  <li>• Cleaning coordination</li>
                  <li>• Emergency response</li>
                  <li>• Maintenance</li>
                  <li>• Pricing optimization</li>
                </ul>
              </div>
            </div>

            {/* Partial Management */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-amber-200">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl mb-6 mx-auto">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 text-center mb-4">Partial Management</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Best For:</h4>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Working professionals
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Multiple properties
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Want some control
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Moderate budget
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Time Required:</h4>
                  <p className="text-2xl font-bold text-slate-900">3-5 hrs/week</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Cost:</h4>
                  <p className="text-2xl font-bold text-amber-600">15-25%</p>
                  <p className="text-xs text-slate-600">of revenue</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-slate-900">Service Includes:</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Guest communication</li>
                  <li>• Cleaning coordination</li>
                  <li>• Emergency response</li>
                  <li>• Basic maintenance</li>
                  <li className="text-slate-400">• You handle: pricing, marketing</li>
                </ul>
              </div>
            </div>

            {/* Full Management */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-emerald-300 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  RECOMMENDED
                </span>
              </div>
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl mb-6 mx-auto">
                <i className="ri-vip-crown-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 text-center mb-4">Full Management</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Best For:</h4>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Busy professionals
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Multiple properties
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Passive income seekers
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      Maximum optimization
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Time Required:</h4>
                  <p className="text-2xl font-bold text-slate-900">0-1 hr/week</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-2">Cost:</h4>
                  <p className="text-2xl font-bold text-emerald-600">25-35%</p>
                  <p className="text-xs text-slate-600">of revenue</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-slate-900">Service Includes:</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Everything in Partial</li>
                  <li>• Dynamic pricing</li>
                  <li>• Professional photography</li>
                  <li>• Marketing optimization</li>
                  <li>• Revenue reporting</li>
                  <li>• Complete peace of mind</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Decision Flowchart */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-slate-300 mb-12">
            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-8 text-center">
              Which Option Is Right for You?
            </h3>
            
            <div className="space-y-6">
              {[
                {
                  question: 'Do you have 10+ hours per week to dedicate to your Minpaku?',
                  yes: 'Consider Self-Management',
                  no: 'Consider Management Service'
                },
                {
                  question: 'Do you have experience with hospitality or customer service?',
                  yes: 'Self-Management might work',
                  no: 'Management Service recommended'
                },
                {
                  question: 'Are you comfortable handling emergencies at 2 AM?',
                  yes: 'Self-Management possible',
                  no: 'Management Service strongly recommended'
                },
                {
                  question: 'Do you speak fluent Japanese and English?',
                  yes: 'Self-Management feasible',
                  no: 'Management Service essential'
                },
                {
                  question: 'Is this your primary income source?',
                  yes: 'Consider Full Management for optimization',
                  no: 'Partial or Full Management recommended'
                }
              ].map((item, index) => (
                <div key={index} className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border-2 border-slate-200">
                  <h4 className="font-bold text-slate-900 mb-4 flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-full text-white text-sm flex-shrink-0">
                      {index + 1}
                    </span>
                    {item.question}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 ml-11">
                    <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4">
                      <p className="text-sm font-semibold text-emerald-800">
                        <strong>YES:</strong> {item.yes}
                      </p>
                    </div>
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                      <p className="text-sm font-semibold text-red-800">
                        <strong>NO:</strong> {item.no}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Our Service CTA */}
          <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-2 border-amber-300 rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-6">
                <i className="ri-service-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-serif font-bold text-slate-900 mb-4">
                Our Full Management Service
              </h3>
              <p className="text-lg text-slate-700 max-w-3xl mx-auto">
                We handle everything from licensing to daily operations, so you can enjoy passive income without the hassle. Our team has real operational experience in Tokyo and knows exactly what it takes to succeed.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                { icon: 'ri-file-list-3-line', title: 'Licensing Support', description: 'Complete application assistance' },
                { icon: 'ri-customer-service-2-line', title: '24/7 Guest Support', description: 'Multilingual communication' },
                { icon: 'ri-line-chart-line', title: 'Revenue Optimization', description: 'Dynamic pricing & marketing' }
              ].map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border-2 border-amber-200 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl mx-auto mb-4">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-slate-700">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <a
                href="/airbnb-management-tokyo"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white text-lg font-bold px-10 py-5 rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-right-circle-line text-2xl"></i>
                View Our Management Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Ready to Start Your Tokyo Minpaku Journey?
          </h2>
          <p className="text-xl text-slate-300 mb-10 leading-relaxed">
            Book a free 30-minute consultation to discuss your property, timeline, and goals. We'll provide personalized advice based on real operational experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white text-lg font-bold px-10 py-5 rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-right-circle-line text-2xl"></i>
              Book Free Consultation
            </a>
            <a
              href="/minpaku-registration-tokyo"
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 text-lg font-bold px-10 py-5 rounded-xl hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line text-2xl"></i>
              View Registration Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
